export enum CategoryId {
  ID_VERIFICATION = "id-verification",
  INCOME = "income",
  CREDIT_HISTORY = "credit-history",
  RENTAL_HISTORY = "rental-history",
  CRIMINAL_HISTORY = "criminal-history",
}

// Shown instead of any background report data once a report has expired
export const EXPIRED_BACKGROUND_REPORTS_MESSAGE = "Reports are expired.";

/**
 * Configuration for category groups displayed in the UI
 */
export const categoryGroups = [
  {
    id: CategoryId.ID_VERIFICATION,
    name: "ID Verification",
  },
  {
    id: CategoryId.INCOME,
    name: "Income",
  },
  {
    id: CategoryId.CREDIT_HISTORY,
    name: "Credit History",
  },
  {
    id: CategoryId.RENTAL_HISTORY,
    name: "Rental History",
  },
  {
    id: CategoryId.CRIMINAL_HISTORY,
    name: "Criminal History",
  },
];

export const backgroundCheckCategories = [
  CategoryId.CREDIT_HISTORY,
  CategoryId.RENTAL_HISTORY,
  CategoryId.CRIMINAL_HISTORY,
] as const;

export type BackgroundCheckCategoryId =
  (typeof backgroundCheckCategories)[number];

export const identityVerificationCategories = [CategoryId.ID_VERIFICATION];

/**
 * Base label templates by category and status
 */
export const labelTemplates = {
  id: {
    fail: "ID Verification Issues",
    warn: "ID Verification Issues",
    pass: "ID Accepted",
  },
  income: {
    fail: "Income Issues",
    warn: "Income could not be verified",
    pass: "Income Verified", // This will typically be overridden with ratio info
  },
  credit: {
    fail: "Credit Issues",
    warn: "Credit Warning", // Often overridden with score
    pass: "No Issues", // Often overridden with score
  },
  rental: {
    fail: "Rental History Issues",
    warn: "Rental History Warning",
    pass: "No Issues",
  },
  criminal: {
    fail: "Criminal History",
    warn: "Criminal History",
    pass: "No Issues",
  },
};

/**
 * Define which statuses qualify for outcome-based labels by category
 */
export const qualifyingStatuses = {
  rental: ["fail", "warn"],
  credit: ["fail", "warn"],
  criminal: ["warn"],
};

/**
 * Define issue detectors for different categories
 * Each detector has keywords to search for in title/description and the label to show
 */
export const issueDetectors = {
  // Match header processIdVerification (["identity-verification"]) so SentiLink
  // Needs Review outcomes (#identity-synthetic-fraud-check-needs-review) do not
  // steal applicant-card chrome when ID Verification also failed (PRODUCT-2429).
  identity: [{ keywords: ["identity-verification"], label: "ID Verification" }],
  income: [{ keywords: ["income"], label: "Income" }],
  rental: [
    { keywords: ["rental", "collection"], label: "Rental Collections" },
    { keywords: ["eviction"], label: "Evictions" },
  ],
  credit: [
    { keywords: ["credit", "bankrupt"], label: "Bankruptcies" },
    { keywords: ["credit", "collection"], label: "Collections" },
    { keywords: ["foreclosure"], label: "Foreclosures" },
    {
      keywords: ["credit", "thin"],
      label: "Thin Credit File",
    },
    { keywords: ["credit", "score"], label: "Low Credit Score" },
  ],
  criminal: [
    { keywords: ["criminal"], label: "Criminal Records" },
    { keywords: ["felonies"], label: "Felonies" },
  ],
  background: [
    { keywords: ["background-reports-run"], label: "No Background Report" },
  ],
};

export const CATEGORY_ENDPOINTS = {
  [CategoryId.ID_VERIFICATION]: "verifications",
  [CategoryId.INCOME]: "income-and-assets-verification",
  [CategoryId.CREDIT_HISTORY]: "credit-and-background-check",
  [CategoryId.RENTAL_HISTORY]: "credit-and-background-check",
  [CategoryId.CRIMINAL_HISTORY]: "credit-and-background-check",
};

export const CATEGORY_ENDPOINTS_V2 = {
  [CategoryId.ID_VERIFICATION]: "verifications",
  [CategoryId.INCOME]: "income-and-assets-verification",
  [CategoryId.CREDIT_HISTORY]: "credit-history",
  [CategoryId.RENTAL_HISTORY]: "rental-history",
  [CategoryId.CRIMINAL_HISTORY]: "criminal-history",
};

/** BGCC cards on deal overview — path depends on app-summary tab layout flags. */
export function isBackgroundCheckCategory(
  categoryId: CategoryId
): categoryId is BackgroundCheckCategoryId {
  return (backgroundCheckCategories as readonly CategoryId[]).includes(
    categoryId
  );
}

/**
 * Application.jsx only registers split BGCC routes when tab restructure is on.
 * Legacy layout uses one combined "credit-and-background-check" tab for all three cards.
 */
export function getBackgroundCheckDetailsPathSegment(
  categoryId: BackgroundCheckCategoryId,
  useSplitBackgroundTabs: boolean
): string {
  const routeMap = useSplitBackgroundTabs
    ? CATEGORY_ENDPOINTS_V2
    : CATEGORY_ENDPOINTS;
  return routeMap[categoryId];
}

/**
 * Builds "/applications/:id/{tab-segment}" for deal-overview "View all details" links.
 * Returns undefined when base path or category mapping is missing (link is omitted in UI).
 */
export function buildApplicationCategoryDetailsLink(
  applicationBasePath: string | undefined | null,
  categoryId: CategoryId,
  options: { useSplitBackgroundTabs?: boolean } = {}
): string | undefined {
  const trimmedBase = applicationBasePath?.trim();
  if (!trimmedBase) {
    return undefined;
  }

  const normalizedBase = trimmedBase.replace(/\/$/, "");

  if (isBackgroundCheckCategory(categoryId)) {
    const segment = getBackgroundCheckDetailsPathSegment(
      categoryId,
      Boolean(options.useSplitBackgroundTabs)
    );
    return segment ? `${normalizedBase}/${segment}` : undefined;
  }

  // ID / Income routes are the same in legacy and restructured layouts.
  const segment = CATEGORY_ENDPOINTS_V2[categoryId];
  return segment ? `${normalizedBase}/${segment}` : undefined;
}
